import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { CATEGORIES, CATEGORY_MAP } from "../data/categories";
import ProductCard from "../components/ProductCard";

export default function Menu() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const initialCat = searchParams.get("cat") || "sandwiches";
    const [active, setActive] = useState(initialCat);

    useEffect(() => {
        setLoading(true);
        api.get("/menu/items")
            .then((r) => {
                setItems(r.data || []);
                setError("");
            })
            .catch((e) => setError("تعذر تحميل القائمة"))
            .finally(() => setLoading(false));
    }, []);

    // Auto-scroll to the initial category once items load
    useEffect(() => {
        if (loading) return;
        const paramCat = searchParams.get("cat");
        if (!paramCat) return;
        const el = document.getElementById(`section-${paramCat}`);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 120;
            window.scrollTo({ top, behavior: "smooth" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const grouped = useMemo(() => {
        const g = {};
        CATEGORIES.forEach((c) => (g[c.key] = []));
        items.forEach((it) => {
            if (g[it.category]) g[it.category].push(it);
        });
        return g;
    }, [items]);

    const setCategory = (key) => {
        setActive(key);
        const sp = new URLSearchParams(searchParams);
        sp.set("cat", key);
        setSearchParams(sp, { replace: true });
        const el = document.getElementById(`section-${key}`);
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 120;
            window.scrollTo({ top, behavior: "smooth" });
        }
    };

    return (
        <div data-testid="menu-page" className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 py-6">
            <div className="mb-4">
                <h1 className="section-title text-3xl sm:text-4xl">المنيو</h1>
                <p className="text-text-muted mt-3 text-sm">اختر أصنافك المفضلة وأضفها للسلة</p>
            </div>

            {/* Category nav */}
            <div className="sticky top-[64px] z-30 -mx-4 px-4 py-2 bg-cream/95 backdrop-blur border-b border-[var(--border-subtle)]">
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setCategory(c.key)}
                            data-testid={`category-tab-${c.key}`}
                            className={`whitespace-nowrap px-4 py-2 rounded-full font-bold text-sm transition ${
                                active === c.key
                                    ? "bg-brand text-white shadow-md"
                                    : "bg-white text-text-main border border-[var(--border-subtle)]"
                            }`}
                        >
                            <i className={`fa-solid ${c.icon} ms-1`} /> {c.name}
                        </button>
                    ))}
                </div>
            </div>

            {loading && (
                <div className="py-12 text-center text-text-muted" data-testid="menu-loading">
                    <i className="fa-solid fa-spinner fa-spin text-2xl text-brand" />
                    <p className="mt-2">جاري التحميل...</p>
                </div>
            )}
            {error && (
                <div className="py-6 text-center text-red-600" data-testid="menu-error">{error}</div>
            )}

            {!loading && CATEGORIES.map((c) => (
                <section key={c.key} id={`section-${c.key}`} className="mt-8 scroll-mt-32">
                    <div className="mb-4 flex items-center gap-3">
                        <div
                            className="w-12 h-12 rounded-2xl bg-cover bg-center"
                            style={{ backgroundImage: `url('${c.image}')` }}
                        />
                        <h2 className="section-title text-2xl">{c.name}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {grouped[c.key]?.map((it) => (
                            <ProductCard key={it.id} item={it} />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}
